import { LitElement, html } from 'lit';
import { customElement, state, property } from 'lit/decorators.js';
import './slideshow.js';
import '@shoelace-style/shoelace/dist/components/dialog/dialog.js';
import '@shoelace-style/shoelace/dist/components/tab-group/tab-group.js';
import '@shoelace-style/shoelace/dist/components/tab/tab.js';
import '@shoelace-style/shoelace/dist/components/tab-panel/tab-panel.js';
import { landingStyles } from './landing-page.styles.js';

@customElement('pristinemaint-landing')
export class PristineMaintLanding extends LitElement {
  @state()
  private contactForm = { name: '', email: '', message: '' };

  @state()
  private formStatus: 'idle' | 'loading' | 'success' | 'error' = 'idle';

  @property({ type: String })
  sectionRequest: string | null = null;

  static styles = landingStyles;

  protected firstUpdated() {
    this.scrollToTop();
  }

  private handleGetStarted() {
    // For now, just show an alert. In the future, this could redirect to a demo or contact form
    alert('Thank you for your interest! Please contact us to get started with PristineMaint.');
  }

  private handleLearnMore() {
    // Scroll to features section
    const featuresSection = this.shadowRoot?.querySelector('.features');
    if (featuresSection) {
      featuresSection.scrollIntoView({ behavior: 'smooth' });
    }
  }

    private navigateTo(event: Event, route: string, section?: string) {
    event.preventDefault();
    this.emitNavigate(route, section);
  }

    private emitNavigate(route: string, section?: string) {
    this.dispatchEvent(
      new CustomEvent('navigate', {
        detail: { route, section },
        bubbles: true,
        composed: true,
      }),
    );
  }
    private openFeaturesDialog(event: Event) {
    event.preventDefault();
    const dialog = this.shadowRoot?.getElementById('featuresDialog') as
      | (HTMLElement & { show?: () => void })
      | null;
    dialog?.show?.();
  }

    private handleInputChange(event: Event) {
    const target = event.target as HTMLInputElement | HTMLTextAreaElement;
    const { name, value } = target;
    this.contactForm = { ...this.contactForm, [name]: value };
  }
  private async handleContactSubmit(event: Event) {
    event.preventDefault();
    this.formStatus = 'loading';
    try {
      // TODO: replace with integration to backend/email service
      console.log('Contact form submitted:', this.contactForm);
      await new Promise((resolve) => setTimeout(resolve, 500));
      this.formStatus = 'success';
      this.contactForm = { name: '', email: '', message: '' };
    } catch (error) {
      console.error('Failed to submit contact form', error);
      this.formStatus = 'error';
    }
  }

    private scrollToTop() {
    window.scrollTo({ top: 0, left: 0, behavior: "smooth" });
  }

  private scrollToFeatures() {
    const featuresSection = this.shadowRoot?.querySelector('.features') as HTMLElement | null;
    if (!featuresSection) return;
    featuresSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }

  private scrollToContact() {
    const contactSection = this.shadowRoot?.querySelector('.contact-section') as HTMLElement | null;
    if (!contactSection) return;
    contactSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
    const firstField = contactSection.querySelector('input[name="name"]') as HTMLInputElement | null;
    if (firstField) {
      setTimeout(() => firstField.focus(), 400);
    }
  }

  protected updated(changedProps: Map<string, unknown>) {
    if (changedProps.has('sectionRequest') && this.sectionRequest) {
      if (this.sectionRequest === 'contact') {
        this.scrollToContact();
      } else if (this.sectionRequest === 'features') {
        this.scrollToFeatures();
      }

      this.dispatchEvent(
        new CustomEvent('section-request-consumed', {
          detail: { section: this.sectionRequest },
          bubbles: true,
          composed: true,
        }),
      );

      this.sectionRequest = null;
    }
  }

  render() {
    return html`
      <div class="landing-container">
        <!-- Header -->
        <header class="header">
          <div class="logo">
            <img 
              src="https://firebasestorage.googleapis.com/v0/b/work-order-managment.firebasestorage.app/o/pristinemaint.png?alt=media&token=2dd117e4-d4e7-4bd6-b666-548c5068b566" 
              alt="PristineMaint Logo" 
              class="logo-image"
            />
            <span class="logo-text">PristineMaint</span>
          </div>
          
          <nav class="nav-links">
            <a href="#features" class="nav-link" @click=${(event: Event) => this.navigateTo(event, '/features')}>Features</a>
            <a href="/pricing" class="nav-link" @click=${(event: Event) => this.navigateTo(event, '/pricing')}>Pricing</a>
            <a href="#about" class="nav-link">About</a>
            <a href="#contact" class="nav-link" @click=${(event: Event) => this.navigateTo(event, '/', 'contact')}>Contact</a>
          </nav>
          
          <div class="header-actions">
            <button class="btn btn-secondary" @click=${this.handleLearnMore}>Learn More</button>
            <button class="btn btn-primary" @click=${this.handleGetStarted}>Get Started</button>
          </div>
        </header>

        <!-- Hero Section -->
        <main class="hero">
          <div class="hero-content">
            <h1 class="hero-title">
              Advanced CMMS Solutions for Modern Businesses
            </h1>
            <p class="hero-subtitle">
              Streamline your maintenance operations with our cutting-edge Computerized Maintenance Management System. 
              Reduce downtime, optimize resources, and maximize efficiency.
            </p>
            <div class="hero-actions">
              <button class="btn btn-primary" @click=${this.handleGetStarted}>
                Start Free Trial
              </button>
              <button class="btn btn-secondary" @click=${this.handleLearnMore}>
                Watch Demo
              </button>
            </div>
          </div>
          
          <div class="hero-image">
            <div class="slideshow-wrapper">
              <pristinemaint-slideshow></pristinemaint-slideshow>
            </div>
          </div>
        </main>

        <!-- Features Section -->
        <section class="features" id="features">
          <div class="features-container">
            <div class="features-header">
              <h2 class="features-title">Powerful Features to Simplify Maintenance</h2>
              <p class="features-subtitle">
                Everything you need to manage assets, work orders, and preventive maintenance — in one intuitive platform.
              </p>
            </div>

            <div class="features-actions">
              <button class="btn btn-secondary" @click=${this.openFeaturesDialog}>
                Explore Interactive Tour
              </button>
            </div>

            <div class="features-grid">
              <div class="feature-card">
                <div class="feature-icon">🗂️</div>
                <h3 class="feature-title">Work Order Management</h3>
                <p class="feature-description">
                  Easily create, assign, and track work orders in real-time. Prioritize tasks, monitor progress, and reduce response times.
                </p>
              </div>

              <div class="feature-card">
                <div class="feature-icon">⏱️</div>
                <h3 class="feature-title">Preventive Maintenance Scheduling</h3>
                <p class="feature-description">
                  Automate PM tasks with smart scheduling to minimize breakdowns and extend asset life.
                </p>
              </div>

              <div class="feature-card">
                <div class="feature-icon">🧰</div>
                <h3 class="feature-title">Asset Management</h3>
                <p class="feature-description">
                  Centralized tracking of equipment, history, warranties, and performance — all in one place.
                </p>
              </div>

              <div class="feature-card">
                <div class="feature-icon">📉</div>
                <h3 class="feature-title">Downtime Tracking & Reporting</h3>
                <p class="feature-description">
                  Log, analyze, and visualize downtime events to identify root causes and improve uptime.
                </p>
              </div>

              <div class="feature-card">
                <div class="feature-icon">📦</div>
                <h3 class="feature-title">Inventory & Spare Parts Control</h3>
                <p class="feature-description">
                  Manage spare parts, reorder levels, and availability to reduce delays in maintenance.
                </p>
              </div>

              <div class="feature-card">
                <div class="feature-icon">📱</div>
                <h3 class="feature-title">Mobile Access</h3>
                <p class="feature-description">
                  Technicians can access, update, and close work orders directly from their phone or tablet.
                </p>
              </div>

              <div class="feature-card">
                <div class="feature-icon">📈</div>
                <h3 class="feature-title">Analytics & Dashboards</h3>
                <p class="feature-description">
                  Customizable KPIs and reports to optimize maintenance strategies and resource allocation.
                </p>
              </div>

              <div class="feature-card">
                <div class="feature-icon">🔐</div>
                <h3 class="feature-title">User Roles & Permissions</h3>
                <p class="feature-description">
                  Flexible access levels for Admins, Engineers, and Shop Floor users to keep data secure.
                </p>
              </div>
            </div>
          </div>
        </section>

        <!-- CTA Section -->
        <section class="cta">
          <div class="cta-container">
            <h2 class="cta-title">Ready to Transform Your Maintenance Operations?</h2>
            <p class="cta-subtitle">
              Join thousands of companies already using PristineMaint to optimize their maintenance processes.
            </p>
            <div class="cta-actions">
              <button class="btn btn-white" @click=${this.handleGetStarted}>
                Start Your Free Trial
              </button>
              <button class="btn btn-outline-white" @click=${this.handleGetStarted}>
                Contact Sales
              </button>
            </div>
          </div>
        </section>

        <!-- Contact Section -->
        <section class="contact-section" id="contact">
          <div class="contact-container">
            <div class="contact-copy">
              <h2>Connect with Our Team</h2>
              <p>
                For more information please contact us on
                <a href="mailto:info@pristinemaint.com">info@pristinemaint.com</a> or leave your details and we will reach out shortly.
              </p>
            </div>

            <form class="contact-form" @submit=${this.handleContactSubmit}>
              <label>
                Name
                <input
                  type="text"
                  name="name"
                  autocomplete="name"
                  .value=${this.contactForm.name}
                  @input=${this.handleInputChange}
                  required
                />
              </label>

              <label>
                Email
                <input
                  type="email"
                  name="email"
                  autocomplete="email"
                  .value=${this.contactForm.email}
                  @input=${this.handleInputChange}
                  required
                />
              </label>

              <label>
                How can we help?
                <textarea
                  name="message"
                  .value=${this.contactForm.message}
                  @input=${this.handleInputChange}
                  required
                ></textarea>
              </label>

              <button class="btn btn-primary" type="submit" ?disabled=${this.formStatus === 'loading'}>
                ${this.formStatus === 'loading' ? 'Sending…' : 'Send Message'}
              </button>

              ${this.formStatus === 'success'
                ? html`<div class="contact-status success">Thanks! We will be in touch soon.</div>`
                : this.formStatus === 'error'
                    ? html`<div class="contact-status error">Something went wrong. Please try again.</div>`
                    : null}
            </form>
          </div>
        </section>

        <!-- Footer -->
        <footer class="footer">
          <div class="footer-container">
            <div class="footer-section">
              <h3>PristineMaint</h3>
              <p>Advanced CMMS solutions for modern businesses. Streamline your maintenance operations with our cutting-edge platform.</p>
            </div>
            
            <div class="footer-section">
              <h3>Product</h3>
              <ul class="footer-links">
                <li><a href="/features" @click=${(event: Event) => this.navigateTo(event, '/features')}>Features</a></li>
                <li><a href="/pricing" @click=${(event: Event) => this.navigateTo(event, '/pricing')}>Pricing</a></li>
                <li><a href="#demo">Demo</a></li>
                <li><a href="#integrations">Integrations</a></li>
              </ul>
            </div>
            
            <div class="footer-section">
              <h3>Company</h3>
              <ul class="footer-links">
                <li><a href="#about">About Us</a></li>
                <li><a href="#careers">Careers</a></li>
                <li><a href="#blog">Blog</a></li>
                <li><a href="#press">Press</a></li>
              </ul>
            </div>
            
            <div class="footer-section">
              <h3>Support</h3>
              <ul class="footer-links">
                <li><a href="#help">Help Center</a></li>
                <li><a href="mailto:info@pristinemaint.com?subject=Support Request&body=Hello, I have a question about PristineMaint.">Contact Us</a></li>

                <li><a href="#status">Status</a></li>
                <li><a href="#security">Security</a></li>
              </ul>
            </div>
          </div>
          
          <div class="footer-bottom">
            <p>&copy; 2024 PristineMaint. All rights reserved.</p>
          </div>
        </footer>
      </div>

      <sl-dialog label="Platform Features" class="features-modal" id="featuresDialog">
        <sl-tab-group>
          <sl-tab slot="nav" panel="dashboard">Dashboard</sl-tab>
          <sl-tab slot="nav" panel="assets">Assets</sl-tab>
          <sl-tab slot="nav" panel="workorders">Work Orders</sl-tab>
          <sl-tab slot="nav" panel="pm">PM</sl-tab>
          <sl-tab slot="nav" panel="parts">Parts</sl-tab>
          <sl-tab slot="nav" panel="locations">Locations</sl-tab>
          <sl-tab slot="nav" panel="hs">Health &amp; Safety</sl-tab>

          <sl-tab-panel name="dashboard">
            <div class="feature-panel">
              <div class="feature-panel-content">
                <h3>Dashboard</h3>
                <div>
                  <h4>Capabilities</h4>
                  <ul>
                    <li>Monitor KPIs, downtime, open work orders, overdue PMs, and incidents.</li>
                    <li>Track asset health with real-time charts and performance widgets.</li>
                    <li>Drill into alerts and trends to identify emerging issues fast.</li>
                  </ul>
                </div>
                <div>
                  <h4>Benefits</h4>
                  <ul>
                    <li>Gives managers instant visibility into maintenance operations.</li>
                    <li>Supports fast decision-making to keep production on track.</li>
                    <li>Reduces unplanned downtime with proactive alerting.</li>
                  </ul>
                </div>
                <p class="feature-panel-meta"><strong>Users:</strong> Admins, Managers</p>
              </div>
              <img src="/assets/screenshots/dashboard.svg" alt="Dashboard overview screenshot" class="feature-screenshot" />
            </div>
          </sl-tab-panel>

          <sl-tab-panel name="assets">
            <div class="feature-panel">
              <div class="feature-panel-content">
                <h3>Asset Management</h3>
                <div>
                  <h4>Capabilities</h4>
                  <ul>
                    <li>Create and manage detailed asset records with hierarchy and locations.</li>
                    <li>Store photos, costs, warranties, and custom data fields.</li>
                    <li>Link assets directly to work orders, PM tasks, and documents.</li>
                  </ul>
                </div>
                <div>
                  <h4>Benefits</h4>
                  <ul>
                    <li>Maintain complete asset history for better lifecycle tracking.</li>
                    <li>Simplify compliance and audit preparation with accurate records.</li>
                    <li>Surface high-cost or high-risk assets for strategic planning.</li>
                  </ul>
                </div>
                <p class="feature-panel-meta"><strong>Users:</strong> Admins, Engineers</p>
              </div>
              <img src="/assets/screenshots/assets.svg" alt="Asset management screenshot" class="feature-screenshot" />
            </div>
          </sl-tab-panel>

          <sl-tab-panel name="workorders">
            <div class="feature-panel">
              <div class="feature-panel-content">
                <h3>Work Orders</h3>
                <div>
                  <h4>Capabilities</h4>
                  <ul>
                    <li>Create corrective and preventive work orders with rich detail.</li>
                    <li>Assign jobs, set priorities (Low → Critical), and attach parts/documents.</li>
                    <li>Track status from open to in-progress to completed with timestamps.</li>
                  </ul>
                </div>
                <div>
                  <h4>Benefits</h4>
                  <ul>
                    <li>Streamlines maintenance workflows and eliminates manual handoffs.</li>
                    <li>Reduces miscommunication between teams and shifts.</li>
                    <li>Provides audit-ready compliance records of every job.</li>
                  </ul>
                </div>
                <p class="feature-panel-meta"><strong>Users:</strong> Technicians, Engineers, Managers</p>
              </div>
              <img src="/assets/screenshots/work-orders.svg" alt="Work orders screenshot" class="feature-screenshot" />
            </div>
          </sl-tab-panel>

          <sl-tab-panel name="pm">
            <div class="feature-panel">
              <div class="feature-panel-content">
                <h3>Preventive Maintenance</h3>
                <div>
                  <h4>Capabilities</h4>
                  <ul>
                    <li>Schedule recurring PM tasks with calendar or meter-based triggers.</li>
                    <li>Auto-generate work orders and update next due dates automatically.</li>
                    <li>Adjust schedules when tasks complete or assets are offline.</li>
                  </ul>
                </div>
                <div>
                  <h4>Benefits</h4>
                  <ul>
                    <li>Cuts unplanned breakdowns and emergency repairs.</li>
                    <li>Improves asset reliability and uptime across the facility.</li>
                    <li>Extends equipment life with disciplined maintenance routines.</li>
                  </ul>
                </div>
                <p class="feature-panel-meta"><strong>Users:</strong> Admins, Engineers</p>
              </div>
              <img src="/assets/screenshots/pm.svg" alt="Preventive maintenance scheduler screenshot" class="feature-screenshot" />
            </div>
          </sl-tab-panel>

          <sl-tab-panel name="parts">
            <div class="feature-panel">
              <div class="feature-panel-content">
                <h3>Parts &amp; Inventory</h3>
                <div>
                  <h4>Capabilities</h4>
                  <ul>
                    <li>Manage spare parts catalogs with stock levels and reorder points.</li>
                    <li>Track usage history, supplier references, and pricing.</li>
                    <li>Reserve parts directly from work orders to maintain availability.</li>
                  </ul>
                </div>
                <div>
                  <h4>Benefits</h4>
                  <ul>
                    <li>Prevents costly stockouts during critical repairs.</li>
                    <li>Reduces excess inventory and carrying costs.</li>
                    <li>Ensures technicians have the right parts when the job starts.</li>
                  </ul>
                </div>
                <p class="feature-panel-meta"><strong>Users:</strong> Storekeepers, Engineers, Managers</p>
              </div>
              <img src="/assets/screenshots/parts.svg" alt="Parts inventory screenshot" class="feature-screenshot" />
            </div>
          </sl-tab-panel>

          <sl-tab-panel name="locations">
            <div class="feature-panel">
              <div class="feature-panel-content">
                <h3>Locations</h3>
                <div>
                  <h4>Capabilities</h4>
                  <ul>
                    <li>Organize assets, teams, and parts by plant, building, or area.</li>
                    <li>Review maintenance history and KPIs at the site level.</li>
                    <li>Visualize asset distribution and workload across locations.</li>
                  </ul>
                </div>
                <div>
                  <h4>Benefits</h4>
                  <ul>
                    <li>Makes multi-site operations easier to coordinate.</li>
                    <li>Delivers faster reporting for regional or site managers.</li>
                    <li>Improves accountability with location-specific metrics.</li>
                  </ul>
                </div>
                <p class="feature-panel-meta"><strong>Users:</strong> Admins, Managers</p>
              </div>
              <img src="/assets/screenshots/locations.svg" alt="Locations performance screenshot" class="feature-screenshot" />
            </div>
          </sl-tab-panel>

          <sl-tab-panel name="hs">
            <div class="feature-panel">
              <div class="feature-panel-content">
                <h3>Health &amp; Safety</h3>
                <div>
                  <h4>Capabilities</h4>
                  <ul>
                    <li>Record incidents, near misses, and corrective actions.</li>
                    <li>Plan audits and safety inspections with follow-up tasks.</li>
                    <li>Link safety issues to work orders and generate compliance reports.</li>
                  </ul>
                </div>
                <div>
                  <h4>Benefits</h4>
                  <ul>
                    <li>Improves workplace safety culture and visibility.</li>
                    <li>Supports regulatory compliance with complete documentation.</li>
                    <li>Reduces risk by closing the loop on hazards quickly.</li>
                  </ul>
                </div>
                <p class="feature-panel-meta"><strong>Users:</strong> Admins, Safety Managers</p>
              </div>
              <img src="/assets/screenshots/hs.svg" alt="Health and safety dashboard screenshot" class="feature-screenshot" />
            </div>
          </sl-tab-panel>
        </sl-tab-group>
      </sl-dialog>
    `;
  }
}
